import { useState, useEffect, FormEvent } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/apiFetch'
import MagnumSays from '@/components/MagnumSays'

const EXERCISE_TYPES = [
  { value: 'WORD_DECODE', label: 'Word Decode' },
  { value: 'SPELLING', label: 'Spelling' },
  { value: 'SYNONYM_MATCH', label: 'Synonym Match' },
  { value: 'SYNONYM_PAIRS', label: 'Synonym Pairs' },
  { value: 'NOTE_NAMING', label: 'Note Naming' },
  { value: 'INTERVAL_RECOGNITION', label: 'Interval Recognition' },
  { value: 'EAR_TRAINING', label: 'Ear Training' },
  { value: 'ACT', label: 'ACT Practice' },
]

interface Goal {
  id: number
  childId: number
  name: string
  type: string
  goalValue: number
  period: string
  exerciseTypes: string | null
  listId: number | null
  currentProgress: number
  status: string
  currentStreak: number
  longestStreak: number
}

interface SpellingList {
  id: number
  name: string
  isActive: boolean
  wordCount?: number
}

interface StreakReward {
  id: number
  goalId: number
  streakLength: number
  rewardType: string
  rewardText: string | null
  status: string
}

interface GoalEntry {
  goal: Goal
  streakRewards: StreakReward[]
}

interface Reward {
  maxPerDay?: number | null
  grantsToday?: number | null
  id: number
  childId: number
  name: string
  rewardType: string
  rewardText: string | null
  triggerMode: string
  status: string
  goalIds: number[]
}

interface FullView {
  goals: GoalEntry[]
  rewards: Reward[]
}

function periodLabel(p: string) {
  return p === 'WEEKLY' ? 'weekly' : 'daily'
}

function typeLabel(t: string) {
  if (t === 'LIST_MASTERY') return 'words mastered'
  if (t === 'ACT_QUESTIONS') return 'ACT questions'
  return t === 'MASTERY' ? 'words mastered' : 'minutes'
}

function exerciseTypeLabels(csv: string | null): string {
  if (!csv) return 'All games'
  return csv.split(',').map(t => {
    const found = EXERCISE_TYPES.find(e => e.value === t)
    return found ? found.label : t
  }).join(', ')
}

// --- Goal Form ---

function GoalForm({ childId, onSave, onCancel, initial }: {
  childId: number
  onSave: (v: { name: string; type: string; goalValue: number; period: string; exerciseTypes: string | null; listId: number | null }) => Promise<void>
  onCancel: () => void
  initial?: Goal
}) {
  const [name, setName] = useState(initial?.name || '')
  const [type, setType] = useState(initial?.type || 'DURATION')
  const [goalValue, setGoalValue] = useState(initial?.goalValue?.toString() || '')
  const [period, setPeriod] = useState(initial?.period || 'DAILY')
  const [selectedTypes, setSelectedTypes] = useState<string[]>(
    initial?.exerciseTypes ? initial.exerciseTypes.split(',') : []
  )
  const [listId, setListId] = useState<number | null>(initial?.listId || null)
  const [spellingLists, setSpellingLists] = useState<SpellingList[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    apiFetch<{ list: SpellingList; wordCount: number }[]>(`/spelling/lists?childId=${childId}`)
      .then(data => setSpellingLists(data.map(d => ({ ...d.list, wordCount: d.wordCount }))))
      .catch(() => {})
  }, [childId])

  const toggleType = (v: string) => {
    setSelectedTypes(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v])
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (type === 'LIST_MASTERY' && !listId) {
      setError('Select a spelling list')
      return
    }
    setSubmitting(true)
    try {
      await onSave({
        name,
        type,
        goalValue: type === 'LIST_MASTERY' ? 0 : parseInt(goalValue, 10),
        period: type === 'LIST_MASTERY' ? 'DAILY' : period,
        exerciseTypes: type === 'LIST_MASTERY'
          ? 'SPELLING'
          : type === 'ACT_QUESTIONS'
            ? 'ACT'
            : (selectedTypes.length > 0 ? selectedTypes.join(',') : null),
        listId: type === 'LIST_MASTERY' ? listId : null,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save.')
    } finally {
      setSubmitting(false)
    }
  }

  const isListMastery = type === 'LIST_MASTERY'

  return (
    <form onSubmit={handleSubmit} className="p-4 border border-border rounded-lg space-y-3 bg-card">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div>
        <label className="block text-sm font-medium mb-1">Goal Name</label>
        <input type="text" required value={name} onChange={e => setName(e.target.value)}
          placeholder={isListMastery ? 'Master spelling list' : 'Daily reading goal'}
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className={isListMastery ? 'col-span-2' : ''}>
          <label className="block text-sm font-medium mb-1">Type</label>
          <select value={type} onChange={e => setType(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm">
            <option value="DURATION">Time (minutes)</option>
            <option value="MASTERY">Words Mastered</option>
            <option value="LIST_MASTERY">Master a Word List</option>
            <option value="ACT_QUESTIONS">ACT Questions Logged</option>
          </select>
        </div>
        {!isListMastery && (
          <div>
            <label className="block text-sm font-medium mb-1">Period</label>
            <select value={period} onChange={e => setPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm">
              <option value="DAILY">Daily</option>
              <option value="WEEKLY">Weekly</option>
            </select>
          </div>
        )}
      </div>

      {isListMastery ? (
        <div>
          <label className="block text-sm font-medium mb-1">Spelling List</label>
          {spellingLists.length > 0 ? (
            <select value={listId || ''} onChange={e => setListId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-sm">
              <option value="">Select a list...</option>
              {spellingLists.map(sl => (
                <option key={sl.id} value={sl.id}>
                  {sl.name}{sl.wordCount != null ? ` (${sl.wordCount} words)` : ''}
                </option>
              ))}
            </select>
          ) : (
            <p className="text-sm text-muted-foreground">No spelling lists found. Create one first in the spelling game settings.</p>
          )}
        </div>
      ) : (
        <>
          <div>
            <label className="block text-sm font-medium mb-1">
              Target ({type === 'DURATION' ? 'minutes' : type === 'ACT_QUESTIONS' ? 'questions' : 'words'} per {period === 'WEEKLY' ? 'week' : 'day'})
            </label>
            <input type="number" required min={1} value={goalValue} onChange={e => setGoalValue(e.target.value)}
              placeholder={type === 'DURATION' ? '15' : type === 'ACT_QUESTIONS' ? '10' : '5'}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Applies to</label>
            <div className="flex flex-wrap gap-2">
              {EXERCISE_TYPES.filter(et => (
                type === 'ACT_QUESTIONS' ? et.value === 'ACT' : et.value !== 'ACT'
              )).map(et => (
                <button key={et.value} type="button" onClick={() => toggleType(et.value)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition ${
                    selectedTypes.includes(et.value)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}>{et.label}</button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {selectedTypes.length === 0 ? 'All games count toward this goal' : `Only ${exerciseTypeLabels(selectedTypes.join(','))} count`}
            </p>
          </div>
        </>
      )}
      <div className="flex gap-2">
        <button type="submit" disabled={submitting}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition disabled:opacity-50 min-h-[44px]">
          {submitting ? 'Saving...' : 'Save Goal'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-accent transition min-h-[44px]">Cancel</button>
      </div>
    </form>
  )
}

// --- Reward Form ---

function RewardForm({ goals, onSave, onCancel }: {
  goals: Goal[]
  onSave: (v: { name: string; rewardText: string; rewardType: string; unlockableType: string | null; unlockableId: string | null; triggerMode: string; maxPerDay: number | null; goalIds: number[] }) => Promise<void>
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [rewardType, setRewardType] = useState('TEXT')
  const [rewardText, setRewardText] = useState('')
  const [screenTimeMinutes, setScreenTimeMinutes] = useState(30)
  const [triggerMode, setTriggerMode] = useState('AND')
  // Null keeps the default: earn it once, then wait for the period to turn over.
  const [maxPerDay, setMaxPerDay] = useState<number | null>(null)
  const [selectedGoals, setSelectedGoals] = useState<number[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const toggleGoal = (id: number) => {
    setSelectedGoals(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (selectedGoals.length === 0) { setError('Select at least one goal'); return }
    setError('')
    setSubmitting(true)
    try {
      const effectiveRewardText = rewardType === 'SCREEN_TIME'
        ? `${screenTimeMinutes} minutes of screen time`
        : rewardText
      await onSave({
        name,
        rewardText: effectiveRewardText,
        rewardType,
        unlockableType: rewardType === 'SCREEN_TIME' ? 'SCREEN_TIME_MINUTES' : null,
        unlockableId: rewardType === 'SCREEN_TIME' ? String(screenTimeMinutes) : null,
        triggerMode,
        maxPerDay,
        goalIds: selectedGoals,
      })
    } catch (err: any) {
      setError(err.message || 'Failed to save.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 border border-border rounded-lg space-y-3 bg-card">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div>
        <label className="block text-sm font-medium mb-1">Reward Name</label>
        <input type="text" required value={name} onChange={e => setName(e.target.value)}
          placeholder="Screen time"
          className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Reward Type</label>
        <div className="flex gap-2">
          <button type="button" onClick={() => setRewardType('TEXT')}
            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
              rewardType === 'TEXT' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
            }`}>Custom Text</button>
          <button type="button" onClick={() => setRewardType('SCREEN_TIME')}
            className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
              rewardType === 'SCREEN_TIME' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
            }`}>Screen Time</button>
        </div>
      </div>
      {rewardType === 'TEXT' ? (
        <div>
          <label className="block text-sm font-medium mb-1">Reward Description</label>
          <input type="text" required value={rewardText} onChange={e => setRewardText(e.target.value)}
            placeholder="30 minutes of screen time"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium mb-1">Minutes to Earn</label>
          <div className="flex items-center gap-2">
            <input type="number" value={screenTimeMinutes} onChange={e => setScreenTimeMinutes(Number(e.target.value))}
              min={5} max={240}
              className="w-24 px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
            <span className="text-sm text-muted-foreground">minutes of TV time</span>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Linked Goals</label>
        <div className="space-y-1">
          {goals.filter(g => g.status === 'ACTIVE').map(g => (
            <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={selectedGoals.includes(g.id)}
                onChange={() => toggleGoal(g.id)} className="accent-primary" />
              {g.name}
            </label>
          ))}
        </div>
        {goals.filter(g => g.status === 'ACTIVE').length === 0 && (
          <p className="text-xs text-muted-foreground">Create a goal first</p>
        )}
      </div>
      {selectedGoals.length > 1 && (
        <div>
          <label className="block text-sm font-medium mb-1">Trigger When</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTriggerMode('AND')}
              className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
                triggerMode === 'AND' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}>All goals met</button>
            <button type="button" onClick={() => setTriggerMode('OR')}
              className={`flex-1 py-2 rounded-lg border text-sm font-medium transition ${
                triggerMode === 'OR' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}>Any goal met</button>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">How often can this be earned?</label>
        <div className="flex gap-2 flex-wrap">
          <button type="button" onClick={() => setMaxPerDay(null)}
            className={`px-3 py-2 rounded-lg border text-sm font-medium transition min-h-[44px] ${
              maxPerDay == null ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
            }`}>Once a day</button>
          {[2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => setMaxPerDay(n)}
              className={`px-3 py-2 rounded-lg border text-sm font-medium transition min-h-[44px] ${
                maxPerDay === n ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground'
              }`}>{n}× a day</button>
          ))}
        </div>
        {maxPerDay != null && (
          <p className="text-xs text-muted-foreground mt-1">
            Earned again each time the goal is met over — hitting a 15 minute goal
            after 45 minutes of reading pays out three times.
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={submitting}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium text-sm hover:opacity-90 transition disabled:opacity-50 min-h-[44px]">
          {submitting ? 'Saving...' : 'Save Reward'}
        </button>
        <button type="button" onClick={onCancel}
          className="px-4 py-2 border border-border rounded-lg text-sm hover:bg-accent transition min-h-[44px]">Cancel</button>
      </div>
    </form>
  )
}

// --- Goal Card ---

function GoalCard({ entry, onUpdate, childId }: { entry: GoalEntry; onUpdate: () => void; childId: number }) {
  const { goal, streakRewards } = entry
  const [editing, setEditing] = useState(false)
  const [addingStreak, setAddingStreak] = useState(false)
  const [streakLength, setStreakLength] = useState('')
  const [streakText, setStreakText] = useState('')

  const handleEdit = async (v: { name: string; type: string; goalValue: number; period: string; exerciseTypes: string | null; listId: number | null }) => {
    await apiFetch(`/goals/${goal.id}`, { method: 'PUT', body: JSON.stringify(v) })
    setEditing(false)
    onUpdate()
  }

  const handleDelete = async () => {
    await apiFetch(`/goals/${goal.id}`, { method: 'DELETE' })
    onUpdate()
  }

  const handleAddStreak = async (e: FormEvent) => {
    e.preventDefault()
    await apiFetch(`/goals/${goal.id}/streak-rewards`, {
      method: 'POST',
      body: JSON.stringify({ streakLength: parseInt(streakLength), rewardText: streakText }),
    })
    setAddingStreak(false)
    setStreakLength('')
    setStreakText('')
    onUpdate()
  }

  const handleRedeemStreak = async (id: number) => {
    await apiFetch(`/goals/streak-rewards/${id}/redeem`, { method: 'POST' })
    onUpdate()
  }

  if (editing) {
    return <GoalForm childId={childId} initial={goal} onSave={handleEdit} onCancel={() => setEditing(false)} />
  }

  const pct = Math.min(100, Math.round((goal.currentProgress / goal.goalValue) * 100))

  return (
    <div className="p-4 border border-border rounded-lg space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-medium">{goal.name}</p>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {goal.type !== 'LIST_MASTERY' && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                {periodLabel(goal.period)}
              </span>
            )}
            {goal.type === 'LIST_MASTERY' ? (
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                Word list mastery
              </span>
            ) : (
              <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {exerciseTypeLabels(goal.exerciseTypes)}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {goal.currentProgress} / {goal.goalValue} {typeLabel(goal.type)}
          </p>
        </div>
        <div className="text-right shrink-0">
          {goal.currentStreak > 0 && (
            <p className="text-sm font-medium text-amber-500">
              🔥 {goal.currentStreak} {goal.period === 'WEEKLY' ? 'wk' : 'day'} streak
            </p>
          )}
          {goal.longestStreak > 1 && (
            <p className="text-xs text-muted-foreground">best: {goal.longestStreak}</p>
          )}
        </div>
      </div>

      {goal.status === 'ACTIVE' && (
        <div className="w-full bg-accent rounded-full h-2.5 overflow-hidden">
          <div className="bg-primary h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%` }} />
        </div>
      )}

      {streakRewards.length > 0 && (
        <div className="space-y-1">
          {streakRewards.map(sr => (
            <div key={sr.id} className={`flex items-center justify-between text-sm p-2 rounded-lg ${
              sr.status === 'EARNED' ? 'bg-amber-50 border border-amber-200' : 'bg-muted/50'
            }`}>
              <span>
                {sr.streakLength} {goal.period === 'WEEKLY' ? 'weeks' : 'days'} in a row
                {sr.rewardText && <span className="text-muted-foreground"> → {sr.rewardText}</span>}
              </span>
              {sr.status === 'EARNED' && (
                <button onClick={() => handleRedeemStreak(sr.id)}
                  className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded font-medium">
                  Redeem
                </button>
              )}
              {sr.status === 'REDEEMED' && <span className="text-xs text-muted-foreground">✓ Redeemed</span>}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {goal.status === 'ACTIVE' && (
          <>
            <button onClick={() => setEditing(true)}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px]">Edit</button>
            <button onClick={() => setAddingStreak(!addingStreak)}
              className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-accent transition min-h-[36px]">
              + Streak Bonus
            </button>
            <button onClick={handleDelete}
              className="text-xs px-3 py-1.5 text-red-500 border border-red-200 rounded-md hover:bg-red-50 transition min-h-[36px]">Delete</button>
          </>
        )}
      </div>

      {addingStreak && (
        <form onSubmit={handleAddStreak} className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">
              {goal.period === 'WEEKLY' ? 'Weeks' : 'Days'} in a row
            </label>
            <input type="number" required min={2} value={streakLength} onChange={e => setStreakLength(e.target.value)}
              placeholder="7" className="w-full px-2 py-1.5 border border-border rounded-lg text-sm" />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-muted-foreground mb-1">Reward</label>
            <input type="text" required value={streakText} onChange={e => setStreakText(e.target.value)}
              placeholder="ice cream trip" className="w-full px-2 py-1.5 border border-border rounded-lg text-sm" />
          </div>
          <button type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium min-h-[36px]">Add</button>
        </form>
      )}
    </div>
  )
}

// --- Reward Card ---

function RewardCard({ reward, goals, onUpdate }: { reward: Reward; goals: Goal[]; onUpdate: () => void }) {
  const linkedNames = reward.goalIds.map(gid => goals.find(g => g.id === gid)?.name || `Goal #${gid}`)
  const isEarned = reward.status === 'EARNED'
  const isRedeemed = reward.status === 'REDEEMED'

  const handleRedeem = async () => {
    await apiFetch(`/goals/rewards/${reward.id}/redeem`, { method: 'POST' })
    onUpdate()
  }

  const handleDelete = async () => {
    await apiFetch(`/goals/rewards/${reward.id}`, { method: 'DELETE' })
    onUpdate()
  }

  return (
    <div className={`p-4 border rounded-lg space-y-2 ${
      isEarned ? 'border-amber-300 bg-amber-50/50' : isRedeemed ? 'border-border opacity-60' : 'border-border'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">{reward.name}</p>
            {isEarned && <span className="text-lg">🏆</span>}
          </div>
          {reward.rewardText && (
            <p className="text-sm mt-0.5">{reward.rewardText}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {linkedNames.length > 1 ? (
              <>{reward.triggerMode === 'AND' ? 'All of' : 'Any of'}: {linkedNames.join(', ')}</>
            ) : (
              <>When: {linkedNames[0]}</>
            )}
          </p>
          {reward.maxPerDay != null && (
            <p className="text-xs font-medium text-primary mt-0.5">
              Repeats: {reward.grantsToday ?? 0} of {reward.maxPerDay} earned today
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {isEarned && (
          <button onClick={handleRedeem}
            className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md font-medium hover:opacity-90 transition min-h-[36px]">
            Mark as Redeemed
          </button>
        )}
        {reward.status === 'PENDING' && (
          <button onClick={handleDelete}
            className="text-xs px-3 py-1.5 text-red-500 border border-red-200 rounded-md hover:bg-red-50 transition min-h-[36px]">Delete</button>
        )}
      </div>
    </div>
  )
}

// --- Page ---

export default function GoalsPage() {
  const { childId } = useParams<{ childId: string }>()
  const [data, setData] = useState<FullView | null>(null)
  const [loading, setLoading] = useState(true)
  const [showAddGoal, setShowAddGoal] = useState(false)
  const [showAddReward, setShowAddReward] = useState(false)

  const fetchAll = async () => {
    try {
      const result = await apiFetch<FullView>(`/goals/full?childId=${childId}`)
      setData(result)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [childId])

  const handleCreateGoal = async (v: { name: string; type: string; goalValue: number; period: string; exerciseTypes: string | null; listId: number | null }) => {
    await apiFetch('/goals', { method: 'POST', body: JSON.stringify({ childId: parseInt(childId!, 10), ...v }) })
    setShowAddGoal(false)
    await fetchAll()
  }

  const handleCreateReward = async (v: { name: string; rewardText: string; rewardType: string; unlockableType: string | null; unlockableId: string | null; triggerMode: string; maxPerDay: number | null; goalIds: number[] }) => {
    await apiFetch('/goals/rewards', {
      method: 'POST',
      body: JSON.stringify({ childId: parseInt(childId!, 10), ...v }),
    })
    setShowAddReward(false)
    await fetchAll()
  }

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-12 text-center"><p className="text-muted-foreground">Loading goals...</p></div>
  }

  const goals = data?.goals || []
  const rewards = data?.rewards || []
  const allGoals = goals.map(e => e.goal)
  const activeGoals = goals.filter(e => e.goal.status === 'ACTIVE')
  const pendingRewards = rewards.filter(r => r.status === 'PENDING')
  const earnedRewards = rewards.filter(r => r.status === 'EARNED')
  const redeemedRewards = rewards.filter(r => r.status === 'REDEEMED')

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <Link to="/dashboard"
        className="text-sm text-muted-foreground hover:text-foreground transition inline-flex items-center gap-1">
        &larr; Back to dashboard
      </Link>

      {/* Goals Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Goals</h2>
          <button onClick={() => setShowAddGoal(!showAddGoal)}
            className="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition min-h-[44px]">
            {showAddGoal ? 'Cancel' : '+ Goal'}
          </button>
        </div>

        {showAddGoal && <GoalForm childId={parseInt(childId!, 10)} onSave={handleCreateGoal} onCancel={() => setShowAddGoal(false)} />}

        {activeGoals.length > 0 ? (
          <div className="space-y-3">
            {activeGoals.map(entry => (
              <GoalCard key={entry.goal.id} entry={entry} onUpdate={fetchAll} childId={parseInt(childId!, 10)} />
            ))}
          </div>
        ) : !showAddGoal && (
          <MagnumSays>
            <p className="font-medium">No goals yet!</p>
            <p className="mt-1 text-muted-foreground">Set a daily or weekly target — even 10 minutes a day builds real reading skills.</p>
          </MagnumSays>
        )}
      </div>

      {/* Rewards Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold">Rewards</h2>
          {allGoals.length > 0 && (
            <button onClick={() => setShowAddReward(!showAddReward)}
              className="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 transition min-h-[44px]">
              {showAddReward ? 'Cancel' : '+ Reward'}
            </button>
          )}
        </div>

        {showAddReward && (
          <RewardForm goals={allGoals} onSave={handleCreateReward} onCancel={() => setShowAddReward(false)} />
        )}

        {earnedRewards.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-600">Earned</h3>
            {earnedRewards.map(r => <RewardCard key={r.id} reward={r} goals={allGoals} onUpdate={fetchAll} />)}
          </div>
        )}

        {pendingRewards.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Active</h3>
            {pendingRewards.map(r => <RewardCard key={r.id} reward={r} goals={allGoals} onUpdate={fetchAll} />)}
          </div>
        )}

        {redeemedRewards.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Redeemed</h3>
            {redeemedRewards.map(r => <RewardCard key={r.id} reward={r} goals={allGoals} onUpdate={fetchAll} />)}
          </div>
        )}

        {rewards.length === 0 && allGoals.length > 0 && !showAddReward && (
          <p className="text-sm text-muted-foreground">No rewards configured. Add a reward to motivate goal completion!</p>
        )}
      </div>
    </div>
  )
}
